import { useState } from "react";
import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { DateTimeField, ProblemError } from "@csa/web-foundation";
import { Badge, Card, Icon, StatCard, Table } from "../../ui";
import { useT } from "../../i18n/index";
import type {
  AdvisorPerformanceRow,
  MetricProvenance,
  MetricsQuery,
} from "../../api/metrics-contracts";
import { BarChart, Donut, StackedBar } from "./charts";
import type { BarDatum } from "./charts";
import {
  useAdvisorEconomics,
  useAdvisorPerformance,
  useConversionBySource,
  useHouseholdDepth,
  useMotorHealthConversion,
  usePremiumExperience,
  useRetention,
} from "./use-reports";

// U10-REPORTS-F — the management pilot-scorecard dashboard ("Raporlar"). Consumes the
// six read-only aggregate rollups (U10-REPORTS-B / gap B) over ONE shared cohort/window
// filter. RBAC-is-UX: the surface is nav-gated on analytics:read @ all and every read is
// re-enforced server-side. NO client-side aggregation — each card renders its server
// rollup verbatim (numerator/denominator + rate, distributions, medians). A zero case is
// a real zero-row, never an error. Charts are dependency-free k-kit primitives (./charts)
// fed the server rollups; there is no time-series read, so no curve is ever fabricated.
// The two cost-side metrics (CAC channel-spend, advisor cost) are flagged "external feed
// pending" as a dignified placeholder — never faked into a number.

/** Format a stored rate (a 0–1 decimal from the API) as a percentage for display. */
function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/** A number-or-em-dash: a null median/mean is "no data to measure", shown as "—". */
function orDash(v: number | string | null): ReactNode {
  return v == null ? "—" : String(v);
}

/** Money is a decimal STRING (numeric(14,2)/TRY) — formatted in ink, tabular, grouped;
 *  NEVER alarm-red. Falls back to the raw string if it is not a finite number. */
const TRY_FMT = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  minimumFractionDigits: 2,
});
function money(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? TRY_FMT.format(n) : v;
}

/** Summarise a from/to pair for the header period chips (ISO in, "all-time" when blank). */
function rangeLabel(from: string, to: string, allLabel: string): string {
  const f = from.trim();
  const tv = to.trim();
  if (!f && !tv) return allLabel;
  return `${f || "…"} → ${tv || "…"}`;
}

export function ReportsDashboard() {
  const { t } = useT();
  // The shared cohort/window inputs. Blank = unbounded (the reads treat a dropped bound
  // as unbounded). One control set feeds all six reads (the query is the cache key).
  const [cohortFrom, setCohortFrom] = useState("");
  const [cohortTo, setCohortTo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const q: MetricsQuery = {
    cohortFrom: cohortFrom.trim() || undefined,
    cohortTo: cohortTo.trim() || undefined,
    from: from.trim() || undefined,
    to: to.trim() || undefined,
  };

  return (
    <section className="surf-stack rep">
      <div className="surf-head rep-head">
        <div>
          <h1 className="surf-title">{t("rep.heading")}</h1>
          <p className="surf-sub muted">{t("rep.subheading")}</p>
        </div>
        <div className="rep-period" data-testid="rep-period">
          <span className="rep-period-chip">
            <Icon name="calendar" size={14} strokeWidth={1.9} />
            {t("rep.cohortChip")}: {rangeLabel(cohortFrom, cohortTo, t("rep.periodAll"))}
          </span>
          <span className="rep-period-chip">
            <Icon name="measure" size={14} strokeWidth={1.9} />
            {t("rep.windowChip")}: {rangeLabel(from, to, t("rep.periodAll"))}
          </span>
        </div>
      </div>

      <Card className="rep-filters" data-testid="rep-filters">
        <DateTimeField
          label={t("rep.cohortFrom")}
          value={cohortFrom}
          onChange={setCohortFrom}
          todayLabel={t("df.today")}
          nowLabel={t("df.now")}
          hint={t("rep.cohortHint")}
          data-testid="cohort-from"
        />
        <DateTimeField
          label={t("rep.cohortTo")}
          value={cohortTo}
          onChange={setCohortTo}
          todayLabel={t("df.today")}
          nowLabel={t("df.now")}
          data-testid="cohort-to"
        />
        <DateTimeField
          label={t("rep.windowFrom")}
          value={from}
          onChange={setFrom}
          todayLabel={t("df.today")}
          nowLabel={t("df.now")}
          hint={t("rep.windowHint")}
          data-testid="window-from"
        />
        <DateTimeField
          label={t("rep.windowTo")}
          value={to}
          onChange={setTo}
          todayLabel={t("df.today")}
          nowLabel={t("df.now")}
          data-testid="window-to"
        />
      </Card>

      <KpiRow q={q} />

      <div className="rep-grid">
        <ConversionBySourceCard q={q} />
        <MotorHealthCard q={q} />
        <RetentionCard q={q} />
        <HouseholdDepthCard q={q} />
        <AdvisorEconomicsCard q={q} />
        <PremiumExperienceCard q={q} />
      </div>

      <AdvisorPerformanceBoard q={q} />
    </section>
  );
}

// ── KPI stat row ────────────────────────────────────────────────────────────────
/** The headline-rate KPIs — the four clean 0–1 rates pulled from the reads that carry
 *  one. Loading is "…", a failed read is an honest em-dash (never a fabricated number).
 *  The reads dedupe against the cards below by react-query cache key. */
function KpiRow({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const conversion = useConversionBySource(q);
  const motorHealth = useMotorHealthConversion(q);
  const retention = useRetention(q);
  const premium = usePremiumExperience(q);

  const rate = (query: UseQueryResult<unknown>, value: number | undefined): ReactNode =>
    query.isLoading ? "…" : query.error != null || value == null ? "—" : pct(value);

  return (
    <div className="rep-kpis" data-testid="rep-kpis">
      <StatCard
        icon="measure"
        iconTone="blue"
        value={rate(conversion, conversion.data?.totals.conversionRate)}
        label={t("rep.kpi.conversion")}
        note={t("rep.kpi.conversionNote")}
      />
      <StatCard
        icon="health"
        iconTone="green"
        value={rate(motorHealth, motorHealth.data?.conversionRate)}
        label={t("rep.kpi.motorHealth")}
        note={t("rep.kpi.motorHealthNote")}
      />
      <StatCard
        icon="protection"
        iconTone="blue"
        value={rate(retention, retention.data?.retentionRate)}
        label={t("rep.kpi.retention")}
        note={t("rep.kpi.retentionNote")}
      />
      <StatCard
        icon="quality"
        iconTone="green"
        value={rate(premium, premium.data?.priceIntegrity.matchRate)}
        label={t("rep.kpi.priceIntegrity")}
        note={t("rep.kpi.priceIntegrityNote")}
      />
    </div>
  );
}

/** The shared card frame: heading + loading/error/(content) states for one read. Empty
 *  is NOT a state here — an empty/zero response is real data the body renders as zeros. */
function ReportCard<T>({
  title,
  testId,
  query,
  wide,
  children,
}: {
  title: string;
  testId: string;
  query: UseQueryResult<T>;
  wide?: boolean;
  children: (data: T) => ReactNode;
}) {
  const { t } = useT();
  return (
    <Card className={`rep-card${wide ? " rep-card--wide" : ""}`} data-testid={testId}>
      <div className="rep-card-head">
        <span className="rep-card-title">{title}</span>
      </div>
      {query.isLoading && (
        <p className="rep-loading muted" data-testid={`${testId}-loading`}>
          <span className="rep-loading-spinner" aria-hidden="true" />
          {t("rep.loading")}
        </p>
      )}
      {query.error != null && (
        <p role="alert" className="form-error" data-testid={`${testId}-error`}>
          {query.error instanceof ProblemError ? query.error.problem.detail : t("rep.error")}
        </p>
      )}
      {query.data != null && children(query.data)}
    </Card>
  );
}

/** A dignified "external feed pending" placeholder — for the cost denominators that live
 *  outside the platform (CAC channel-spend, advisor cost / gap A). A calm bordered block
 *  with a lock glyph + the pending chip + why; NEVER a fabricated number or broken dash. */
function ExternalFeedPending({ label }: { label: string }) {
  const { t } = useT();
  return (
    <div className="rep-external" data-testid="external-feed-pending">
      <span className="rep-external-icon" aria-hidden="true">
        <Icon name="lock" size={15} strokeWidth={1.9} />
      </span>
      <div className="rep-external-body">
        <Badge variant="warning">{t("rep.externalPending")}</Badge>
        <p className="rep-external-desc muted">{label}</p>
      </div>
    </div>
  );
}

// ── R1 — Conversion by source ──────────────────────────────────────────────────
function ConversionBySourceCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useConversionBySource(q);
  return (
    <ReportCard title={t("rep.r1.title")} testId="r1" query={query}>
      {(data) => {
        const bars: BarDatum[] = data.bySource.map((r) => ({
          label: r.source,
          value: r.conversionRate,
          display: pct(r.conversionRate),
        }));
        return (
          <>
            <p className="muted rep-desc">{t("rep.r1.desc")}</p>
            {data.bySource.length === 0 ? (
              <p className="muted" data-testid="r1-empty">
                {t("rep.emptyRows")}
              </p>
            ) : (
              <>
                <BarChart data={bars} ariaLabel={t("rep.r1.title")} tone="blue" />
                <Table className="rep-table">
                  <thead>
                    <tr>
                      <th>{t("rep.r1.source")}</th>
                      <th>{t("rep.r1.leads")}</th>
                      <th>{t("rep.r1.conversions")}</th>
                      <th>{t("rep.rate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bySource.map((r) => (
                      <tr key={r.source} data-testid="r1-row">
                        <td>{r.source}</td>
                        <td className="rep-num">{r.leads}</td>
                        <td className="rep-num">{r.conversions}</td>
                        <td className="rep-num">{pct(r.conversionRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr data-testid="r1-totals">
                      <td>{t("rep.total")}</td>
                      <td className="rep-num">{data.totals.leads}</td>
                      <td className="rep-num">{data.totals.conversions}</td>
                      <td className="rep-num">{pct(data.totals.conversionRate)}</td>
                    </tr>
                  </tfoot>
                </Table>
              </>
            )}
            <ExternalFeedPending label={t("rep.r1.cacPending")} />
          </>
        );
      }}
    </ReportCard>
  );
}

// ── R2 — Motor → health conversion ─────────────────────────────────────────────
function MotorHealthCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useMotorHealthConversion(q);
  return (
    <ReportCard title={t("rep.r2.title")} testId="r2" query={query}>
      {(data) => (
        <>
          <p className="muted rep-desc">{t("rep.r2.desc")}</p>
          <div className="rep-gauge">
            <Donut value={data.conversionRate} caption={t("rep.r2.converted")} tone="green" />
            <ul className="rep-stats">
              <li>
                {t("rep.r2.motorHh")}: <strong>{data.motorHouseholds}</strong>
              </li>
              <li>
                {t("rep.r2.converted")}: <strong>{data.converted}</strong>
              </li>
              <li>
                {t("rep.r2.medianDays")}: <strong>{orDash(data.medianDaysToConvert)}</strong>
              </li>
              <li>
                {t("rep.r2.sameDayAttach")}: <strong>{data.sameDayAttach}</strong>
              </li>
              <li>
                {t("rep.r2.crossSellIntent")}: <strong>{data.crossSellIntentHouseholds}</strong>
              </li>
            </ul>
          </div>
          <p className="muted rep-rule">{data.rule}</p>
        </>
      )}
    </ReportCard>
  );
}

// ── R4 — Retention / renewal ────────────────────────────────────────────────────
function RetentionCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useRetention(q);
  return (
    <ReportCard title={t("rep.r4.title")} testId="r4" query={query}>
      {(data) => (
        <>
          <p className="muted rep-desc">{t("rep.r4.desc")}</p>
          <div className="rep-gauge">
            <Donut
              value={data.retentionRate}
              caption={t("rep.r4.renewed")}
              tone="blue"
              valueTestId="rate-value"
              ariaLabel={t("rep.r4.title")}
            />
            <div className="rep-gauge-body">
              <p className="muted rep-gauge-den">
                {data.eligible} {t("rep.r4.eligible")}
              </p>
              <StackedBar
                segments={[
                  { label: t("rep.r4.renewed"), value: data.renewed, tone: "green" },
                  { label: t("rep.r4.lapsed"), value: data.lapsed, tone: "amber" },
                ]}
              />
            </div>
          </div>
        </>
      )}
    </ReportCard>
  );
}

// ── R3 — Household depth (multi-life attach + depth) ───────────────────────────
function histogramBars(histogram: Record<string, number>): BarDatum[] {
  return Object.entries(histogram).map(([bucket, count]) => ({ label: bucket, value: count }));
}

function HouseholdDepthCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useHouseholdDepth(q);
  return (
    <ReportCard title={t("rep.r3.title")} testId="r3" query={query} wide>
      {(data) => (
        <div className="rep-dist-grid">
          <div className="rep-dist">
            <h3>{t("rep.r3.attach")}</h3>
            <p className="muted rep-dist-meta">
              {t("rep.r3.healthHh")}: {data.multiLifeAttach.healthHouseholds} · {t("rep.mean")}:{" "}
              {orDash(data.multiLifeAttach.mean)} · {t("rep.median")}:{" "}
              {orDash(data.multiLifeAttach.median)} · {t("rep.min")}: {data.multiLifeAttach.min} ·{" "}
              {t("rep.max")}: {data.multiLifeAttach.max}
            </p>
            <BarChart
              data={histogramBars(data.multiLifeAttach.histogram)}
              testId="histogram"
              ariaLabel={t("rep.r3.attach")}
            />
          </div>
          <div className="rep-dist">
            <h3>{t("rep.r3.depth")}</h3>
            <p className="muted rep-dist-meta">
              {t("rep.r3.households")}: {data.householdDepth.households} · {t("rep.mean")}:{" "}
              {orDash(data.householdDepth.mean)} · {t("rep.median")}:{" "}
              {orDash(data.householdDepth.median)} · {t("rep.min")}: {data.householdDepth.min} ·{" "}
              {t("rep.max")}: {data.householdDepth.max}
            </p>
            <BarChart
              data={histogramBars(data.householdDepth.histogram)}
              testId="histogram"
              ariaLabel={t("rep.r3.depth")}
            />
          </div>
        </div>
      )}
    </ReportCard>
  );
}

// ── R5 — Advisor unit-economics ─────────────────────────────────────────────────
function AdvisorEconomicsCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useAdvisorEconomics(q);
  return (
    <ReportCard title={t("rep.r5.title")} testId="r5" query={query} wide>
      {(data) => (
        <>
          <p className="muted rep-desc">{t("rep.r5.desc")}</p>
          {data.byAdvisor.length === 0 ? (
            <p className="muted" data-testid="r5-empty">
              {t("rep.emptyRows")}
            </p>
          ) : (
            <Table className="rep-table">
              <thead>
                <tr>
                  <th>{t("rep.r5.advisor")}</th>
                  <th>{t("rep.r5.households")}</th>
                  <th>{t("rep.r5.policies")}</th>
                  <th>{t("rep.r5.received")}</th>
                  <th>{t("rep.r5.expected")}</th>
                  <th>{t("rep.r5.revPerHh")}</th>
                </tr>
              </thead>
              <tbody>
                {data.byAdvisor.map((r) => (
                  <tr key={r.advisorId} data-testid="r5-row">
                    <td>{r.displayName ?? r.advisorId}</td>
                    <td className="rep-num">{r.households}</td>
                    <td className="rep-num">{r.policies}</td>
                    <td className="rep-money">{money(r.commissionReceived)}</td>
                    <td className="rep-money">{money(r.commissionExpected)}</td>
                    <td className="rep-money">{money(r.revenuePerHousehold)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr data-testid="r5-totals">
                  <td>
                    {t("rep.total")} ({data.totals.advisors})
                  </td>
                  <td className="rep-num">{data.totals.households}</td>
                  <td className="rep-num">{data.totals.policies}</td>
                  <td className="rep-money">{money(data.totals.commissionReceived)}</td>
                  <td className="rep-money">{money(data.totals.commissionExpected)}</td>
                  {/* A11 — was a hard-coded em dash. The book-level ratio is now served
                      (total received / total households), so the footer stops being the
                      one cell in the table that refuses to answer. */}
                  <td className="rep-money" data-testid="r5-total-revperhh">
                    {money(data.totals.revenuePerHousehold)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          )}
          <ExternalFeedPending label={t("rep.r5.costPending")} />
        </>
      )}
    </ReportCard>
  );
}

// ── R6 — Premium-experience panel ────────────────────────────────────────────────
function PremiumExperienceCard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = usePremiumExperience(q);
  return (
    <ReportCard title={t("rep.r6.title")} testId="r6" query={query} wide>
      {(data) => (
        <div className="rep-panel">
          <section className="rep-panel-cell">
            <h3>{t("rep.r6.priceIntegrity")}</h3>
            <div className="rep-gauge">
              <Donut
                value={data.priceIntegrity.matchRate}
                caption={t("rep.r6.matched")}
                tone="blue"
                ariaLabel={t("rep.r6.priceIntegrity")}
              />
              <ul className="rep-stats">
                <li>
                  {t("rep.r6.checked")}: <strong>{data.priceIntegrity.checked}</strong>
                </li>
                <li>
                  {t("rep.r6.matched")}: <strong>{data.priceIntegrity.matched}</strong>
                </li>
              </ul>
            </div>
          </section>
          <section className="rep-panel-cell">
            <h3>{t("rep.r6.issuanceSpeed")}</h3>
            <ul className="rep-stats">
              <li>
                {t("rep.r6.issued")}: <strong>{data.issuanceSpeed.issued}</strong>
              </li>
              <li>
                {t("rep.r6.medianHours")}: <strong>{orDash(data.issuanceSpeed.medianHours)}</strong>
              </li>
              <li>
                {t("rep.r6.p90Hours")}: <strong>{orDash(data.issuanceSpeed.p90Hours)}</strong>
              </li>
              <li>
                {t("rep.r6.maxHours")}: <strong>{orDash(data.issuanceSpeed.maxHours)}</strong>
              </li>
            </ul>
          </section>
          <section className="rep-panel-cell">
            <h3>{t("rep.r6.noSpam")}</h3>
            <ul className="rep-stats">
              <li>
                {t("rep.r6.households")}: <strong>{data.noSpam.households}</strong>
              </li>
              <li>
                {t("rep.r6.maxTouches")}: <strong>{data.noSpam.maxTouches}</strong>
              </li>
              <li>
                {t("rep.r6.meanTouches")}: <strong>{orDash(data.noSpam.meanTouches)}</strong>
              </li>
              <li>
                {t("rep.r6.breaches")}: <strong>{data.noSpam.breaches}</strong>
              </li>
              <li>
                {t("rep.r6.touchCap")}:{" "}
                <strong>
                  {data.noSpam.configVersion
                    ? `${orDash(data.noSpam.configVersion.touchCapCount)} (v${data.noSpam.configVersion.version})`
                    : t("rep.r6.noConfig")}
                </strong>
              </li>
            </ul>
          </section>
          <section className="rep-panel-cell">
            <h3>{t("rep.r6.claimsAdvocacy")}</h3>
            <div className="rep-gauge">
              <Donut
                value={data.claimsAdvocacy.paidRate}
                caption={t("rep.r6.paid")}
                tone="green"
                ariaLabel={t("rep.r6.claimsAdvocacy")}
              />
              <ul className="rep-stats">
                <li>
                  {t("rep.r6.claims")}: <strong>{data.claimsAdvocacy.claims}</strong>
                </li>
                <li>
                  {t("rep.r6.resolved")}: <strong>{data.claimsAdvocacy.resolvedOutcome}</strong>
                </li>
                <li>
                  {t("rep.r6.firstResponse")}:{" "}
                  <strong>{orDash(data.claimsAdvocacy.firstResponseMedianHours)}</strong>
                </li>
                <li>
                  {t("rep.r6.resolution")}:{" "}
                  <strong>{orDash(data.claimsAdvocacy.resolutionMedianDays)}</strong>
                </li>
                <li>
                  {t("rep.r6.satisfaction")}:{" "}
                  <strong>{orDash(data.claimsAdvocacy.satisfactionAvg)}</strong> (
                  {data.claimsAdvocacy.satisfactionCount})
                </li>
              </ul>
            </div>
          </section>
          <section className="rep-panel-cell">
            <h3>{t("rep.r6.namedAdvisor")}</h3>
            <ul className="rep-stats">
              <li>
                {t("rep.r6.households")}: <strong>{data.namedAdvisor.households}</strong>
              </li>
              <li>
                {t("rep.r6.singleOwner")}:{" "}
                <strong data-testid="r6-single-owner">
                  {data.namedAdvisor.singleOwnerIntegrity ? t("rep.yes") : t("rep.no")}
                </strong>
              </li>
              <li>
                {t("rep.r6.reassignments")}: <strong>{data.namedAdvisor.reassignments}</strong>
              </li>
            </ul>
          </section>
        </div>
      )}
    </ReportCard>
  );
}

// ── Advisor-performance board — per-advisor SERVICE QUALITY ──────────────────────
// The genuinely-new Raporlar content (GET /v1/metrics/advisor-performance): a calm,
// name-ordered board of each advisor's service-quality signals — SLA adherence, renewal
// retention, offer→accept conversion, claims advocacy, household depth, no-spam discipline,
// care-hold handling. NO commission field exists here and NO advisor is ranked by any
// figure — rows mirror the backend's `displayName` ordering, so this is never a league
// table. Realized + current-state ONLY (no forecast). Every figure carries its provenance
// (source relation + basis + the instant/window measured) so it is auditable at source.
// Honest-empty: no advisors in scope → a calm empty state; a zero subsection renders zeros;
// a null mean/median is an em-dash, never a fabricated average.

/** Render an ISO instant as a readable local timestamp for the "as of" affordance. A
 *  non-parseable value falls back to the raw string (never silently dropped). */
function asOfLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** A calm, auditable provenance line for one figure — the source relation (full text in a
 *  tooltip, truncated inline) + the measurement basis + the instant/window it covers. This
 *  is the "ground at source" affordance; it is not a metric, so it is rendered muted. */
function Provenance({ p }: { p: MetricProvenance }) {
  const { t } = useT();
  const when =
    p.asOf != null
      ? `${t("rep.ap.asOf")} ${asOfLabel(p.asOf)}`
      : p.window != null
        ? `${t("rep.windowChip")}: ${rangeLabel(p.window.from ?? "", p.window.to ?? "", t("rep.periodAll"))}`
        : null;
  return (
    <p className="rep-prov muted" data-testid="rep-prov">
      <Icon name="records" size={11} strokeWidth={1.9} aria-hidden="true" />
      <span className="rep-prov-src" title={p.source}>
        {p.source}
      </span>
      <span className="rep-prov-meta">
        {p.basis}
        {when != null ? ` · ${when}` : ""}
      </span>
    </p>
  );
}

/** One advisor's card: the seven service-quality signals in a calm panel, each with its
 *  own figure + provenance. Ordered by the parent (displayName); never ranked. */
function AdvisorCard({ row }: { row: AdvisorPerformanceRow }) {
  const { t } = useT();
  const sq = row.serviceQuality;
  const cap = sq.noSpamDiscipline.configVersion;
  return (
    <Card className="rep-card rep-card--wide rep-adv" data-testid="advisor-perf-row">
      <div className="rep-card-head rep-adv-head">
        <span className="rep-card-title" data-testid="advisor-perf-name">
          {row.displayName ?? row.advisorId}
        </span>
      </div>
      <div className="rep-panel">
        <section className="rep-panel-cell">
          <h3>{t("rep.ap.sla")}</h3>
          <div className="rep-gauge">
            <Donut
              value={sq.slaAdherence.adherenceRate}
              caption={t("rep.ap.onTime")}
              tone="blue"
              ariaLabel={t("rep.ap.sla")}
            />
            <ul className="rep-stats">
              <li>
                {t("rep.ap.open")}: <strong>{sq.slaAdherence.open}</strong>
              </li>
              <li>
                {t("rep.ap.onTime")}: <strong>{sq.slaAdherence.onTime}</strong>
              </li>
              <li>
                {t("rep.ap.atRisk")}: <strong>{sq.slaAdherence.atRisk}</strong>
              </li>
              <li>
                {t("rep.ap.breached")}: <strong>{sq.slaAdherence.breached}</strong>
              </li>
            </ul>
          </div>
          <Provenance p={sq.slaAdherence.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.retention")}</h3>
          <p className="muted rep-gauge-den">
            {sq.renewalRetention.eligible} {t("rep.r4.eligible")} · {t("rep.rate")}:{" "}
            {pct(sq.renewalRetention.retentionRate)}
          </p>
          <StackedBar
            segments={[
              { label: t("rep.r4.renewed"), value: sq.renewalRetention.renewed, tone: "green" },
              { label: t("rep.r4.lapsed"), value: sq.renewalRetention.lapsed, tone: "amber" },
            ]}
          />
          <Provenance p={sq.renewalRetention.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.conversion")}</h3>
          <div className="rep-gauge">
            <Donut
              value={sq.conversion.acceptRate}
              caption={t("rep.ap.accepted")}
              tone="green"
              ariaLabel={t("rep.ap.conversion")}
            />
            <ul className="rep-stats">
              <li>
                {t("rep.ap.offersSent")}: <strong>{sq.conversion.offersSent}</strong>
              </li>
              <li>
                {t("rep.ap.accepted")}: <strong>{sq.conversion.accepted}</strong>
              </li>
            </ul>
          </div>
          <Provenance p={sq.conversion.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.claims")}</h3>
          <div className="rep-gauge">
            <Donut
              value={sq.claimsAdvocacy.paidRate}
              caption={t("rep.ap.paid")}
              tone="blue"
              ariaLabel={t("rep.ap.claims")}
            />
            <ul className="rep-stats">
              <li>
                {t("rep.ap.claimsCount")}: <strong>{sq.claimsAdvocacy.claims}</strong>
              </li>
              <li>
                {t("rep.ap.resolved")}: <strong>{sq.claimsAdvocacy.resolvedOutcome}</strong>
              </li>
              <li>
                {t("rep.ap.resolutionDays")}:{" "}
                <strong>{orDash(sq.claimsAdvocacy.resolutionMedianDays)}</strong>
              </li>
              <li>
                {t("rep.ap.satisfaction")}:{" "}
                <strong>{orDash(sq.claimsAdvocacy.satisfactionAvg)}</strong> (
                {sq.claimsAdvocacy.satisfactionCount})
              </li>
            </ul>
          </div>
          <Provenance p={sq.claimsAdvocacy.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.depth")}</h3>
          <ul className="rep-stats">
            <li>
              {t("rep.r3.households")}: <strong>{sq.householdDepth.households}</strong>
            </li>
            <li>
              {t("rep.mean")}:{" "}
              <strong>{orDash(sq.householdDepth.policiesPerHousehold.mean)}</strong>
            </li>
            <li>
              {t("rep.median")}:{" "}
              <strong>{orDash(sq.householdDepth.policiesPerHousehold.median)}</strong>
            </li>
          </ul>
          <Provenance p={sq.householdDepth.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.noSpam")}</h3>
          <ul className="rep-stats">
            <li>
              {t("rep.ap.suppressed")}: <strong>{sq.noSpamDiscipline.suppressedItems}</strong>
            </li>
            <li>
              {t("rep.ap.capBreaches")}: <strong>{sq.noSpamDiscipline.touchCapBreaches}</strong>
            </li>
            <li>
              {t("rep.r6.touchCap")}:{" "}
              <strong>
                {cap ? `${orDash(cap.touchCapCount)} (v${cap.version})` : t("rep.r6.noConfig")}
              </strong>
            </li>
          </ul>
          <Provenance p={sq.noSpamDiscipline.provenance} />
        </section>

        <section className="rep-panel-cell">
          <h3>{t("rep.ap.careHold")}</h3>
          <ul className="rep-stats">
            <li>
              {t("rep.ap.careHoldHh")}: <strong>{sq.careHoldHandling.careHoldHouseholds}</strong>
            </li>
          </ul>
          <Provenance p={sq.careHoldHandling.provenance} />
        </section>
      </div>
    </Card>
  );
}

/** The advisor-performance section — a calm heading (service-quality only, name-ordered,
 *  not ranked, no commission), the board's own "as of" + scope, then one card per advisor.
 *  Loading/error mirror the report cards; an empty board is an honest calm state. */
function AdvisorPerformanceBoard({ q }: { q: MetricsQuery }) {
  const { t } = useT();
  const query = useAdvisorPerformance(q);
  const data = query.data;
  return (
    <section className="rep-board" data-testid="advisor-perf">
      <div className="rep-board-head">
        <div>
          <h2 className="rep-board-title">{t("rep.ap.heading")}</h2>
          <p className="muted rep-desc">{t("rep.ap.subheading")}</p>
        </div>
        {data != null ? (
          <span className="rep-period-chip" data-testid="advisor-perf-asof">
            <Icon name="calendar" size={14} strokeWidth={1.9} />
            {t("rep.ap.asOf")}: {asOfLabel(data.asOf)}
          </span>
        ) : null}
      </div>

      {query.isLoading && (
        <p className="rep-loading muted" data-testid="advisor-perf-loading">
          <span className="rep-loading-spinner" aria-hidden="true" />
          {t("rep.loading")}
        </p>
      )}
      {query.error != null && (
        <p role="alert" className="form-error" data-testid="advisor-perf-error">
          {query.error instanceof ProblemError ? query.error.problem.detail : t("rep.error")}
        </p>
      )}
      {data != null &&
        (data.byAdvisor.length === 0 ? (
          <Card className="rep-card" data-testid="advisor-perf-empty">
            <p className="muted rep-desc">{t("rep.ap.empty")}</p>
          </Card>
        ) : (
          <div className="rep-board-grid">
            {data.byAdvisor.map((row) => (
              <AdvisorCard key={row.advisorId} row={row} />
            ))}
          </div>
        ))}
    </section>
  );
}
